import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { doc, onSnapshot } from 'firebase/firestore';
import { Share2, Check, HelpCircle, X, MousePointerClick, Search, MapPinned, AlertTriangle } from 'lucide-react';
import CoverageSearch from '../coverage/CoverageSearch';
import CoverageResults from '../coverage/CoverageResults';
import CoverageMap from '../coverage/CoverageMap';
import StreetView from '../coverage/StreetView';
import { checkCoverage, createLocationSession, reverseGeocode } from '../../services/apiService';
import { logActivity } from '../../services/activityLogService';

const CoveragePage = ({ user, db, theme, darkMapEnabled = false }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [selectedCoords, setSelectedCoords] = useState(null);
    const [mapData, setMapData] = useState(null);
    const [sessionLink, setSessionLink] = useState(null);
    const [copied, setCopied] = useState(false);
    const [searchInputValue, setSearchInputValue] = useState('');
    const [searchedAddress, setSearchedAddress] = useState('');
    const [showHelp, setShowHelp] = useState(false);

    // Ref for cleanup of Firestore listener
    const sessionUnsubscribeRef = React.useRef(null);
    const activeCoverageRequestRef = React.useRef(0);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (sessionUnsubscribeRef.current) {
                sessionUnsubscribeRef.current();
            }
        };
    }, []);

    const copyLinkToClipboard = async () => {
        try {
            let link = sessionLink;

            // Generate link on demand if not exists
            if (!link) {
                setIsLoading(true);
                try {
                    const response = await createLocationSession();
                    if (response.success && response.sessionId) {
                        link = `${window.location.origin}/locate/${response.sessionId}`;
                        setSessionLink(link);

                        // Start Listening
                        if (db) {
                            if (sessionUnsubscribeRef.current) sessionUnsubscribeRef.current();

                            const docRef = doc(db, 'location_sessions', response.sessionId);
                            sessionUnsubscribeRef.current = onSnapshot(docRef, async (docSnap) => {
                                if (docSnap.exists()) {
                                    const data = docSnap.data();
                                    if (data.status === 'completed' && data.location) {
                                        // Update search immediately
                                        handleSearch(data.location);

                                        // Reverse Geocode to update input field
                                        try {
                                            const revRes = await reverseGeocode(data.location.lat, data.location.lng);
                                            if (revRes && revRes.success && revRes.data) {
                                                const addr = revRes.data;
                                                const formatted = `${addr.street || ''}, ${addr.neighborhood || ''}, ${addr.city || ''}`.replace(/^, /, '').replace(/, ,/g, ',');
                                                setSearchInputValue(formatted);
                                            }
                                        } catch (err) {
                                            console.error("Reverse geocode failed:", err);
                                        }
                                    }
                                }
                            });
                        }
                    }
                } catch (error) {
                    console.error("Erro ao criar sessão de localização:", error);
                    return; // Abort copy if failed
                } finally {
                    setIsLoading(false);
                }
            }

            if (link) {
                await navigator.clipboard.writeText(link);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }

        } catch (err) {
            console.error('Falha ao gerar ou copiar link:', err);
        }
    };

    // Access control logic: 'administrador', 'tecnico', 'atendimento', 'comercial' can access.
    // If user role is NOT one of these, deny access.
    // However, MainTabs already filters visibility. This is a secondary check.
    const allowedRoles = ['administrador', 'tecnico', 'atendimento', 'comercial'];
    if (user && !allowedRoles.includes(user.role)) {
        return (
            <div className="flex items-center justify-center h-full text-slate-500">
                Acesso restrito.
            </div>
        );
    }

    const handleSearch = async (coords, multipleResults = null, address = '', locationType = 'street', locationId = '') => {
        if (multipleResults) {
            // TODO: Handle multiple results selection UI here if needed.
            // For now, auto-select the first one as MVP fallback.
            const first = multipleResults[0];
            coords = { lat: parseFloat(first.lat), lng: parseFloat(first.lon) };
            if (!address && first.display_name) address = first.display_name;
        }

        if (!coords) return;

        const requestId = ++activeCoverageRequestRef.current;

        const finalAddress = address || searchInputValue || `${coords.lat}, ${coords.lng}`;
        setSelectedCoords(coords);
        setSearchedAddress(finalAddress);
        setIsLoading(true);
        setResult(null);
        setMapData(null);

        // Log the search activity
        logActivity('search', 'local', 'coverage_search', null, finalAddress);

        try {
            // Use the centralized API service which handles tokens and base URLs
            const response = await checkCoverage(coords.lat, coords.lng, finalAddress, locationType, locationId);

            // A previous, slower coverage request must never overwrite the
            // condominium/address that the user searched most recently.
            if (requestId !== activeCoverageRequestRef.current) return;

            if (response && response.success) {
                setResult(response.data);
                setMapData(response.data.mapData);
            } else {
                setResult({ viable: false, reason: 'Resposta inesperada do servidor.', details: {} });
            }

        } catch (error) {
            if (requestId !== activeCoverageRequestRef.current) return;
            console.error("Coverage check error:", error);
            // Parse error message from the service
            const message = error.message || 'Erro ao verificar cobertura.';
            setResult({
                viable: false,
                reason: message,
                details: {}
            });
        } finally {
            if (requestId === activeCoverageRequestRef.current) setIsLoading(false);
        }
    };

    const handleSearchStart = () => {
        // Invalidate both the visible result and any request still in flight.
        // If the new name is not found, the page must stay empty instead of
        // continuing to present the last successful condominium as its result.
        activeCoverageRequestRef.current += 1;
        setIsLoading(false);
        setResult(null);
        setMapData(null);
        setSelectedCoords(null);
        setSearchedAddress('');
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8 h-full overflow-y-auto bg-slate-50 dark:bg-slate-900 min-h-screen">
            <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-4">
                    <h1 className="text-xl font-semibold text-slate-800 dark:text-slate-100">Mapa de Cobertura</h1>
                    <button
                        onClick={() => setShowHelp(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                        title="Ajuda sobre o Mapa de Cobertura"
                    >
                        <HelpCircle className="w-4 h-4" />
                        Ajuda
                    </button>
                </div>
            </div>

            {createPortal(
            <AnimatePresence>
                {showHelp && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
                        onClick={() => setShowHelp(false)}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 10 }}
                            transition={{ duration: 0.2 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]"
                        >
                            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50 shrink-0">
                                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                                    <HelpCircle className="w-5 h-5 text-blue-600" />
                                    Ajuda — Mapa de Cobertura
                                </h3>
                                <button
                                    onClick={() => setShowHelp(false)}
                                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                                    title="Fechar"
                                    aria-label="Fechar"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="p-6 overflow-y-auto space-y-6 text-sm text-slate-700 dark:text-slate-300">

                                {/* Como pesquisar */}
                                <section>
                                    <h4 className="flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-100 mb-2">
                                        <Search className="w-4 h-4 text-blue-600" />
                                        Como pesquisar um endereço
                                    </h4>
                                    <p className="mb-2">
                                        Para melhores resultados na busca, digite o endereço no <strong>padrão americano</strong>, com o <strong>número antes do nome da rua</strong>, seguido sempre da <strong>cidade</strong>:
                                    </p>
                                    <div className="bg-slate-100 dark:bg-slate-900 rounded-lg p-3 font-mono text-xs mb-2">
                                        1200 Rua Amazonas, Botucatu
                                    </div>
                                    <p className="mb-2">
                                        Também funciona com coordenadas decimais (<span className="font-mono text-xs">-22.704528, -48.357556</span>) ou em GMS (<span className="font-mono text-xs">22° 42' 16" S, 48° 21' 27" W</span>).
                                    </p>
                                    <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                                        <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                                        <p className="text-amber-800 dark:text-amber-300 text-xs">
                                            Nunca pesquise apenas o nome da rua ou "Rua X, 1200" sem a cidade — endereços sem cidade podem retornar um local errado, em outra cidade ou até em outro estado.
                                        </p>
                                    </div>
                                </section>

                                {/* Confirmar no Street View */}
                                <section>
                                    <h4 className="flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-100 mb-2">
                                        <MapPinned className="w-4 h-4 text-blue-600" />
                                        Confira o endereço no Street View
                                    </h4>
                                    <p>
                                        Depois de pesquisar, sempre confira as imagens do Street View (parte inferior da tela) para garantir que o local encontrado é realmente o endereço pesquisado:
                                    </p>
                                    <ul className="list-disc list-inside mt-2 space-y-1">
                                        <li>Confirme o <strong>nome da rua</strong> nas placas visíveis na imagem.</li>
                                        <li>Confirme a <strong>numeração do imóvel</strong> na placa/fachada, quando visível.</li>
                                        <li>Verifique se o ponto marcado no mapa corresponde de fato ao imóvel pesquisado, e não a um vizinho ou ao lado oposto da rua.</li>
                                    </ul>
                                </section>

                                {/* Nomes de condomínio */}
                                <section>
                                    <h4 className="flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-100 mb-2">
                                        <Search className="w-4 h-4 text-blue-600" />
                                        Pesquisando por condomínio/prédio
                                    </h4>
                                    <p className="mb-2">
                                        Ao buscar pelo nome de um condomínio ou prédio, o sistema perguntará se a busca é por <strong>Nome do Local</strong> ou por <strong>Endereço</strong>. Use nomes no formato cadastrado no sistema, por exemplo:
                                    </p>
                                    <div className="bg-slate-100 dark:bg-slate-900 rounded-lg p-3 font-mono text-xs space-y-1">
                                        <div>Condomínio Village Real</div>
                                        <div>Residencial Parque das Flores</div>
                                        <div>Edifício Solar das Águas</div>
                                    </div>
                                </section>

                                {/* Funcionalidades da página */}
                                <section>
                                    <h4 className="flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-100 mb-2">
                                        <MapPinned className="w-4 h-4 text-blue-600" />
                                        Funcionalidades da página
                                    </h4>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li><strong>Localização do Cliente:</strong> gera um link para o cliente compartilhar a própria localização via GPS; o mapa atualiza automaticamente quando ele confirmar.</li>
                                        <li><strong>Resultado da consulta:</strong> mostra se o local é viável (verde) ou inviável (vermelho), a CTO mais próxima e a distância até ela.</li>
                                        <li><strong>Marcador do endereço:</strong> ícone de casa verde (viável) ou vermelho (inviável), com um círculo de 200m ao redor indicando a área de referência.</li>
                                        <li><strong>Caixas (CTO/CEO):</strong> pontos amarelos são CTOs implantadas, cinzas são CTOs não implantadas (indisponíveis), e ciano são CEOs. A caixa com borda magenta é a mais próxima do endereço pesquisado.</li>
                                        <li><strong>Rota laranja tracejada:</strong> caminho sugerido do cabeamento entre a CTO mais próxima e o endereço, seguindo o traçado das ruas.</li>
                                        <li><strong>Polígonos:</strong> áreas roxas indicam setores/OLTs; áreas azul-claro indicam condomínios horizontais; ícones azuis de prédio indicam condomínios verticais.</li>
                                        <li><strong>Postes e cabos:</strong> pontos cinzas são postes; linhas coloridas são os cabos da rede.</li>
                                        <li><strong>Tela cheia:</strong> botão no canto superior direito do mapa para expandir a visualização.</li>
                                        <li><strong>Street View:</strong> painel inferior direito, sincronizado com o local pesquisado ou clicado.</li>
                                    </ul>
                                </section>

                                {/* Clique direito */}
                                <section>
                                    <h4 className="flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-100 mb-2">
                                        <MousePointerClick className="w-4 h-4 text-blue-600" />
                                        Clique com o botão direito no mapa
                                    </h4>
                                    <p className="mb-2">Clicando com o botão direito em qualquer ponto do mapa, um menu é exibido com as opções:</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li><strong>Trocar modo de Cor:</strong> alterna o mapa entre os temas claro e escuro.</li>
                                        <li><strong>Screenshot:</strong> copia para a área de transferência uma imagem do mapa com o endereço, coordenadas e CTO mais próxima.</li>
                                        <li><strong>Ver no Street View:</strong> abre o Street View no ponto exato clicado no mapa.</li>
                                    </ul>
                                </section>

                                {/* Dica final */}
                                <div className="flex items-start gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                                    <AlertTriangle className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                                    <p className="text-blue-800 dark:text-blue-300 text-xs">
                                        <strong>Dica:</strong> antes de informar qualquer dado ao cliente (viabilidade, distância, CTO), confira atentamente todas as informações no mapa e no Street View. Um endereço ou coordenada incorreta pode levar a uma informação de viabilidade equivocada.
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>,
            document.body
            )}

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">

                {/* Left Column: Search & Results */}
                <div className="flex flex-col space-y-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        {/* Link Sharing Box */}
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6 flex items-center justify-between">
                            <div className="flex-1 mr-4">
                                <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-1">Localização do Cliente</h3>
                                {sessionLink ? (
                                    <>
                                        <p className="text-xs text-blue-600 dark:text-blue-400 truncate">{sessionLink}</p>
                                        <p className="text-[10px] text-blue-500 mt-1">Válido por 30 minutos. A página atualizará automaticamente quando o cliente confirmar.</p>
                                    </>
                                ) : (
                                    <p className="text-xs text-blue-600 dark:text-blue-400">Clique para gerar e copiar um link de compartilhamento.</p>
                                )}
                            </div>
                            <button
                                onClick={copyLinkToClipboard}
                                disabled={isLoading}
                                className="p-2 rounded-full hover:bg-blue-100 dark:hover:bg-blue-800 transition-colors text-blue-600 dark:text-blue-400 disabled:opacity-50"
                                title="Gerar e Copiar Link"
                            >
                                {copied ? <Check className="w-5 h-5" /> : <Share2 className="w-5 h-5" />}
                            </button>
                        </div>

                        <CoverageSearch
                            onSearch={handleSearch}
                            onSearchStart={handleSearchStart}
                            isLoading={isLoading}
                            externalInput={searchInputValue}
                        />

                        {isLoading && (
                            <div className="flex justify-center p-8">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                            </div>
                        )}

                        {!isLoading && result && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                            >
                                <CoverageResults
                                    result={result}
                                    onConfirmBuilding={(building) => {
                                        // Update the map to focus on the selected building/condo
                                        if (building.lat && building.lng) {
                                            setSelectedCoords({ lat: parseFloat(building.lat), lng: parseFloat(building.lng) });
                                        }

                                        setResult(prev => ({
                                            ...prev,
                                            reason: 'Viabilidade técnica confirmada via seleção.',
                                            details: {
                                                ...prev.details,
                                                building: building.name
                                            }
                                        }));
                                    }}
                                />
                            </motion.div>
                        )}
                    </motion.div>
                </div>

                {/* Right Column: Maps (Split View) */}
                <div className="flex flex-col space-y-4 h-[600px] lg:h-auto">
                    {/* Top: Network Map */}
                    <div className="flex-1 min-h-[300px] rounded-xl overflow-hidden shadow-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                        <CoverageMap
                            userLocation={selectedCoords ? [selectedCoords.lat, selectedCoords.lng] : null}
                            mapData={mapData}
                            viable={result?.viable}
                            nearestCto={result?.details?.nearestCto}
                            theme={theme}
                            darkMapEnabled={darkMapEnabled}
                            address={searchedAddress}
                            onRequestStreetView={setSelectedCoords}
                            exportedBy={user?.displayName}
                        />
                    </div>

                    {/* Bottom: Street View */}
                    <div className="flex-1 min-h-[300px] rounded-xl overflow-hidden shadow-sm border border-slate-200 dark:border-slate-700 bg-black">
                        <StreetView coords={selectedCoords} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CoveragePage;
